import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { TripRes } from 'api/swagger';
import type { CreateSoldTripPDFDto } from 'api/swagger';
import { useGenerateSoldTripPdf } from 'api/trips/use-generate-sold-trip-pdf';
import { useGetCargoTrips } from 'api/trips/use-get-cargo-trips';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { useDownloadFile } from 'shared/hooks/use-download-file';
import { formatDate } from 'shared/utils/time/format-date';
import { FormDrawerWithButton } from 'components/common/form-drawer-with-button';
import { TripSoldPdfGeneratorForm } from 'components/trips/trip-sold-pdf-generation/components/trip-sold-pdf-generator-form';
import type { ITripSoldPdfGeneratorFormFields } from 'components/trips/trip-sold-pdf-generation/components/trip-sold-pdf-generator-form';

interface ITripSoldPdfGenerationProps {
  trip: TripRes;
}

const TripSoldPdfGenerationComponent: FC<ITripSoldPdfGenerationProps> = ({ trip }) => {
  const { t } = useTranslation();
  const { mutateAsync: generateSoldPdf, isLoading } = useGenerateSoldTripPdf(trip.id);
  const { data: cargoTrips } = useGetCargoTrips(trip.id);
  const downloadFile = useDownloadFile();

  const handleFinish = async (values: ITripSoldPdfGeneratorFormFields) => {
    const apiVariables: CreateSoldTripPDFDto = {
      cargoData: values.cargoData,
      sendersData: values.sendersData,
      recipientData: values.recipientData,
      loadingDate: formatDate(values.loadingDate),
      unloadingDate: formatDate(values.unloadingDate),
      price: values.price || 0,
      currency: values.currency,
      amountOfDays: values.amountOfDays || 0,
      exportCustomOffice: values.exportCustomOffice,
      importCustomOffice: values.importCustomOffice,
      remarks: values.remarks,
    };
    const result = await generateSoldPdf(apiVariables);

    if (result) {
      downloadFile(result?.url);
    }
  };

  const firstCargoTrip = cargoTrips?.[0];

  if (!firstCargoTrip || !trip.soldToPartners) {
    return null;
  }

  return (
    <FormDrawerWithButton
      formComponent={TripSoldPdfGeneratorForm}
      formProps={{ trip, cargoTrip: firstCargoTrip }}
      title={t('trip:generate')}
      buttonLabel={t('trip:generate')}
      buttonType='default'
      isLoading={isLoading}
      onFinish={handleFinish}
    />
  );
};

export const TripSoldPdfGeneration = withRoleGuard(TripSoldPdfGenerationComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.transport_manager,
  UserRole.customer_manager,
]);
